// components/TestRunner/rules/Rule10_SOR_RefractiveIndex.js
import { RuleUtils } from "../../../utils/RuleUtils.js";

/**
 * Rule10_SOR_RefractiveIndex.js
 *
 * ✅ Règle 10 — Les indices de réfraction des fichiers SOR doivent correspondre aux longueurs d'onde:
 * - 1310 nm => 1.4675
 * - 1550 nm => 1.4680
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Extraction wavelength et indexRefraction des fichiers pvReussite et pvEchec
 * • Validation correspondance : 1310nm → 1.4675 / 1550nm → 1.468
 * • Détection valeurs incorrectes ou Métadonnées manquantes
 * • Contrôle sur tous les fichiers SOR de chaque fibre
 */

export class Rule10_SOR_RefractiveIndex {
  // Constantes des indices de réfraction attendus
  static EXPECTED_INDEX_1310 = 1.4675;
  static EXPECTED_INDEX_1550 = 1.468;

  /**
   * Appliquer la règle 10 : Vérification des indices de réfraction
   * @param {Array} allFibers - Toutes les fibres du projet
   */
  static apply(allFibers) {
    // Analyser chaque fibre individuellement
    allFibers.forEach((fiber) => {
      const fiberAnalysis = this._analyzeFiberRefractiveIndex(fiber);

      // Si des incohérences sont détectées, ajouter une erreur à la fibre
      if (fiberAnalysis.hasInconsistencies) {
        this._addError(
          fiber,
          "R10",
          `Indices de réfraction incorrects dans les fichiers SOR`,
          this._generateErrorReport(fiber, fiberAnalysis)
        );
      }
    });
  }

  /**
   * Analyser les indices de réfraction d'une fibre
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzeFiberRefractiveIndex(fiber) {
    const analysis = {
      fiberId: fiber.id,
      hasInconsistencies: false,
      incorrectFiles: [],
    };

    // Analyser les fichiers pvReussite
    if (fiber.hasPvReussite && fiber.pvReussite && fiber.pvReussite?.files) {
      this._analyzeFiles(fiber.pvReussite.files, "Réussite", analysis);
    }

    // Analyser les fichiers pvEchec
    if (fiber.hasPvEchec && fiber.pvEchec && fiber.pvEchec?.files) {
      this._analyzeFiles(fiber.pvEchec.files, "Échec", analysis);
    }

    return analysis;
  }

  /**
   * Analyser les fichiers d'un type PV
   * @param {Array} files - Tableau des fichiers
   * @param {string} pvType - Type PV (Réussite/Échec)
   * @param {Object} analysis - Objet d'analyse à modifier
   */
  static _analyzeFiles(files, pvType, analysis) {
    if (!Array.isArray(files)) return;

    files.forEach((file, index) => {
      if (!file.metadata) return;

      // Vérifier d'abord si les données existent
      if (file.metadata.wavelength === undefined || file.metadata.indexRefraction === undefined) {
        analysis.hasInconsistencies = true;
        analysis.incorrectFiles.push({
          fileIndex: index,
          fileName: file.name || `Fichier ${index + 1}`,
          pvType: pvType,
          wavelength: file.metadata.wavelength,
          indexRefraction: file.metadata.indexRefraction,
          issue: "Métadonnées manquantes",
          expected: "wavelength et indexRefraction requis",
        });
        return;
      }

      // Convertir avec parseFloat (comme convenu)
      const wavelength = parseFloat(file.metadata.wavelength);
      const indexRefraction = parseFloat(file.metadata.indexRefraction);

      // Vérifier si la conversion a échoué
      if (isNaN(wavelength) || isNaN(indexRefraction)) {
        analysis.hasInconsistencies = true;
        analysis.incorrectFiles.push({
          fileIndex: index,
          fileName: file.name || `Fichier ${index + 1}`,
          pvType: pvType,
          wavelength: file.metadata.wavelength,
          indexRefraction: file.metadata.indexRefraction,
          issue: "Métadonnées invalides",
          expected: "wavelength et indexRefraction doivent être des nombres",
        });
        return;
      }

      // Vérifier longueur d'onde supportée
      const expectedIndex = this._getExpectedIndex(wavelength);

      if (expectedIndex === null) {
        analysis.hasInconsistencies = true;
        analysis.incorrectFiles.push({
          fileIndex: index,
          fileName: file.name || `Fichier ${index + 1}`,
          pvType: pvType,
          wavelength: wavelength,
          indexRefraction: indexRefraction,
          issue: "Longueur d'onde non supportée",
          expected: "1310nm ou 1550nm attendu",
        });
        return;
      }

      // Vérifier valeur indice
      if (indexRefraction !== expectedIndex) {
        analysis.hasInconsistencies = true;
        analysis.incorrectFiles.push({
          fileIndex: index,
          fileName: file.name || `Fichier ${index + 1}`,
          pvType: pvType,
          wavelength: wavelength,
          indexRefraction: indexRefraction,
          issue: "Valeur indice de réfraction incorrecte",
          expected: expectedIndex,
        });
      }
    });
  }

  /**
   * Obtenir l'indice de réfraction attendu pour une longueur d'onde
   * @param {number} wavelength - Longueur d'onde
   * @returns {number|null} Indice attendu ou null si Longueur d'onde non supportée
   */
  static _getExpectedIndex(wavelength) {
    switch (wavelength) {
      case 1310:
        return this.EXPECTED_INDEX_1310;
      case 1550:
        return this.EXPECTED_INDEX_1550;
      default:
        return null;
    }
  }

  /**
   * Générer le rapport d'erreur
   * @param {Object} fiber - Objet fibre
   * @param {Object} fiberAnalysis - Résultat de l'analyse
   * @returns {string} Rapport HTML formaté
   */
  static _generateErrorReport(fiber, fiberAnalysis) {
    let report = `<strong>⚠️ Problème détecté :</strong><br>`;
    report += `Indices de réfraction incorrects dans les fichiers SOR<br><br>`;

    report += `<strong>Détail du problème :</strong><br>`;
    report += `- Fibre: ${RuleUtils.formatFiberId(fiber.id)}<br>`;
    report += `- Fichiers avec erreurs: ${fiberAnalysis.incorrectFiles.length}<br><br>`;

    fiberAnalysis.incorrectFiles.forEach((fileError) => {
      report += `<strong>${fileError.fileName} (${fileError.pvType}):</strong><br>`;

      if (fileError.issue === "Métadonnées manquantes") {
        report += `&nbsp;&nbsp;• Problème: ${fileError.issue}<br>`;
        report += `&nbsp;&nbsp;• Wavelength: ${fileError.wavelength || "manquant"}<br>`;
        report += `&nbsp;&nbsp;• IndexRefraction: ${fileError.indexRefraction || "manquant"}<br>`;
      } else if (fileError.issue === "Format métadonnées SOR invalide") {
        report += `&nbsp;&nbsp;• Problème: ${fileError.issue}<br>`;
        report += `&nbsp;&nbsp;• Wavelength original: ${fileError.wavelength}<br>`;
        report += `&nbsp;&nbsp;• IndexRefraction original: ${fileError.indexRefraction}<br>`;
      } else if (fileError.issue === "Longueur d'onde non supportée") {
        report += `&nbsp;&nbsp;• Wavelength: ${fileError.wavelength}nm<br>`;
        report += `&nbsp;&nbsp;• Problème: ${fileError.issue}<br>`;
        report += `&nbsp;&nbsp;• Attendu: ${fileError.expected}<br>`;
      } else {
        report += `&nbsp;&nbsp;• Wavelength: ${fileError.wavelength}nm<br>`;
        report += `&nbsp;&nbsp;• Indice actuel: ${fileError.indexRefraction}<br>`;
        report += `&nbsp;&nbsp;• Indice attendu: ${fileError.expected}<br>`;
      }
      report += `<br>`;
    });

    report += `<strong>Règles d'indices :</strong><br>`;
    report += `- indexRefraction 1310 nm = ${this.EXPECTED_INDEX_1310}<br>`;
    report += `- indexRefraction 1550 nm = ${this.EXPECTED_INDEX_1550}<br><br>`;

    report += `<strong>⚡ Action requise :</strong><br>`;
    report += `- Corriger les indices de réfraction dans les fichiers SOR<br>`;
    report += `- Vérifier la configuration de l'équipement de mesure<br>`;
    report += `- Reprendre les mesures si nécessaire avec les bons paramètres`;

    return report;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
